// Service Worker Registration and Push Notifications
export const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered:', registration.scope);
      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return null;
    }
  }
  return null;
};

// Request Push Notification Permission
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    console.log('This browser does not support notifications');
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  return false;
};

// Show Local Notification
export const showNotification = (title, options = {}) => {
  if (Notification.permission === 'granted') {
    const notification = new Notification(title, {
      icon: '/logo.png',
      badge: '/logo.png',
      vibrate: [100, 50, 100],
      ...options
    });
    
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
    
    return notification;
  }
  return null;
};

// Request Camera Permission
export const requestCameraPermission = async () => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true });
    // Stop the stream immediately, we just wanted permission
    stream.getTracks().forEach(track => track.stop());
    return true;
  } catch (error) {
    console.error('Camera permission denied:', error);
    return false;
  }
};

// Get Camera Stream
export const getCameraStream = async (facingMode = 'environment') => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode }
    });
    return stream;
  } catch (error) {
    console.error('Failed to get camera stream:', error);
    return null;
  }
};

// Check if app is installed (standalone mode)
export const isAppInstalled = () => {
  return window.matchMedia('(display-mode: standalone)').matches ||
         window.navigator.standalone === true;
};

// Subscribe to Push Notifications — fetches VAPID public key from backend
export const subscribeToPush = async (registration, apiBase) => {
  try {
    // Fetch VAPID public key from backend (auto-generated on first call)
    const apiRoot = apiBase || process.env.REACT_APP_BACKEND_URL || '';
    const vapidResp = await fetch(`${apiRoot}/api/push/vapid-key`);
    if (!vapidResp.ok) throw new Error('VAPID key fetch failed');
    const { public_key } = await vapidResp.json();
    if (!public_key) throw new Error('No VAPID public key configured on server');

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(public_key),
    });
    // Post the subscription to backend so it can send pushes
    const sub = subscription.toJSON();
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || '';
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    await fetch(`${apiRoot}/api/push/subscribe`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ endpoint: sub.endpoint, keys: sub.keys }),
    });
    return subscription;
  } catch (error) {
    console.error('Failed to subscribe to push:', error);
    return null;
  }
};

// Unsubscribe
export const unsubscribeFromPush = async (registration, apiBase) => {
  try {
    const apiRoot = apiBase || process.env.REACT_APP_BACKEND_URL || '';
    const sub = await registration.pushManager.getSubscription();
    if (sub) {
      const endpoint = sub.endpoint;
      await sub.unsubscribe();
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token') || '';
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      await fetch(`${apiRoot}/api/push/unsubscribe`, {
        method: 'POST', headers,
        body: JSON.stringify({ endpoint }),
      });
    }
    return true;
  } catch (e) { console.error(e); return false; }
};

// Check existing subscription
export const getPushSubscription = async () => {
  try {
    const reg = await navigator.serviceWorker.ready;
    return await reg.pushManager.getSubscription();
  } catch { return null; }
};

// Helper function for VAPID key
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
